import { Patient } from '../components/patients/PatientCard';

export interface PatientVitalSigns {
  id?: number;
  patient_id: number;
  appointment_id?: number;
  
  // Raw vital signs values (nested under vital_signs object)
  vital_signs?: {
    systolic_bp?: number;
    diastolic_bp?: number;
    heart_rate?: number;
    respiratory_rate?: number;
    temperature?: string | number;
    oxygen_saturation?: number;
    weight?: string | number;
    height?: string | number;
    bmi?: string | number;
    pain_scale?: number;
  };
  
  // Measurement information (nested under measurement_info object)
  measurement_info?: {
    // measured_by?: number;
    measured_at: string;
    measured_at_formatted?: string;
    notes?: string;
    measured_by?: {
      id: number;
      name: string;
      role: string;
    };
  };
  
  // Relationships
  patient?: Patient;
  appointment?: {
    id: number;
    appointment_date: string;
    appointment_time: string;
    status: string;
  };
  
  // Display Values
  display_values?: {
    blood_pressure?: string;
    temperature?: string;
    heart_rate?: string;
    respiratory_rate?: string;
    oxygen_saturation?: string;
    weight?: string;
    height?: string;
    bmi?: string;
    pain_level?: string;
  };
  
  // Assessments
  assessments?: {
    blood_pressure?: {
      status: string;
      color: string;
      display: string;
    };
    temperature?: {
      status: string;
      color: string;
      display: string;
    };
    heart_rate?: {
      status: string;
      color: string;
      value: number;
    };
    oxygen_saturation?: {
      status: string;
      color: string;
      value: number;
    };
    bmi?: {
      category: string;
      color: string;
      value: number;
    };
    pain?: {
      level: string;
      color: string;
      scale: number;
    };
    overall?: {
      status: string;
      color: string;
    };
  };
  
  // Normal ranges reference
  normal_ranges?: {
    [key: string]: {
      min: number;
      max: number;
      unit: string;
    };
  };
  
  // Range compliance
  within_normal_ranges?: {
    [key: string]: boolean | null;
  };
  
  // Additional Fields
  time_since_measurement?: string;
  is_recent?: boolean;
  has_abnormal_values?: boolean;
  requires_attention?: boolean;
  alerts?: Array<{
    type: string;
    severity: string;
    message: string;
    vital: string;
    value: string;
  }>;
  
  // Notes (can be at root level too)
  notes?: string;
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

// Keep your existing interfaces unchanged
export interface CreateVitalSignsData {
  patient_id: number;
  appointment_id?: number;
  systolic_bp?: number;
  diastolic_bp?: number;
  heart_rate?: number;
  respiratory_rate?: number;
  temperature?: number;
  oxygen_saturation?: number;
  weight?: number;
  height?: number;
  bmi?: number;
  pain_scale?: number;
  measured_at?: string;
  notes?: string;
}

export interface VitalSignsStatistics {
  total_records: number;
  today_records: number;
  this_week_records: number;
  abnormal_bp_count: number;
  fever_count: number;
  high_pain_count: number;
  patients_monitored: number;
}

export interface VitalSignsTrends {
  period_days: number;
  total_records: number;
  trends: {
    blood_pressure: {
      systolic: { avg: number; min: number; max: number };
      diastolic: { avg: number; min: number; max: number };
    };
    heart_rate: { avg: number; min: number; max: number };
    temperature: { avg: number; min: number; max: number };
    weight: {
      latest: number;
      previous: number;
      change: { amount: number; percentage: number; direction: string; significant: boolean };
    };
  };
  abnormal_readings: {
    high_bp_count: number;
    fever_count: number;
    high_pain_count: number;
  };
}

export interface LaravelPaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

class VitalSignsService {
  private static BASE_URL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(`${this.BASE_URL}${url}`, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  // Get vital signs with filtering
  static async getVitalSigns(params?: {
    patient_id?: number;
    appointment_id?: number;
    measured_by?: number;
    date_from?: string;
    date_to?: string;
    recent_days?: number;
    abnormal_bp?: boolean;
    fever?: boolean;
    pain_level?: number;
    search?: string;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<LaravelPaginatedResponse<PatientVitalSigns>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `/vital-signs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await this.request(url);
  }

  // Get single vital signs record
  static async getVitalSignsRecord(id: number): Promise<PatientVitalSigns> {
    const result = await this.request(`/vital-signs/${id}`);
    return result.data;
  }

  // Create new vital signs record
  static async createVitalSigns(data: CreateVitalSignsData): Promise<PatientVitalSigns> {
    try {
      const result = await this.request('/vital-signs', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Create vital signs error:', error);
      throw error;
    }
  }

  // Update vital signs record
  static async updateVitalSigns(id: number, data: Partial<CreateVitalSignsData>): Promise<PatientVitalSigns> {
    const result = await this.request(`/vital-signs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return result.data;
  }

  // Delete vital signs record
  static async deleteVitalSigns(id: number): Promise<void> {
    await this.request(`/vital-signs/${id}`, {
      method: 'DELETE',
    });
  }

  // Get patient's vital signs
  static async getPatientVitalSigns(patientId: number, limit: number = 10): Promise<PatientVitalSigns[]> {
    try {
      const result = await this.request(`/vital-signs/patient/${patientId}?limit=${limit}`);
      
      if (result.success && result.data) {
        return Array.isArray(result.data) ? result.data : [];
      } else if (result.data && Array.isArray(result.data)) {
        return result.data;
      } else if (Array.isArray(result)) {
        return result;
      } else {
        console.warn('Unexpected response structure:', result);
        return [];
      }
    } catch (error) {
      console.error('Get patient vital signs error:', error);
      throw error;
    }
  }

  // Get patient's vital signs trends
  static async getPatientTrends(patientId: number, days: number = 30): Promise<VitalSignsTrends> {
    const result = await this.request(`/vital-signs/patient/${patientId}/trends?days=${days}`);
    return result.data;
  }

  // Get latest vital signs for patient
  static async getLatestVitalSigns(patientId: number): Promise<PatientVitalSigns | null> {
    try {
      const result = await this.request(`/vital-signs/patient/${patientId}/latest`);
      return result.data;
    } catch (error) {
      console.error('Get latest vital signs error:', error);
      return null;
    }
  }

  // Quick record vital signs
  static async quickRecord(data: {
    patient_id: number;
    appointment_id?: number;
    systolic_bp?: number;
    diastolic_bp?: number;
    heart_rate?: number;
    temperature?: number;
    weight?: number;
    pain_scale?: number;
  }): Promise<PatientVitalSigns> {
    try {
      const result = await this.request('/vital-signs/quick-record', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Quick record vital signs error:', error);
      throw error;
    }
  }

  // Get vital signs statistics
  static async getStatistics(): Promise<VitalSignsStatistics> {
    const result = await this.request('/vital-signs/analytics/statistics');
    return result.data;
  }

  // Get normal ranges
  static async getNormalRanges(age?: number): Promise<any> {
    const result = await this.request(`/vital-signs/analytics/normal-ranges${age ? `?age=${age}` : ''}`);
    return result.data;
  }

  // Get alerts for abnormal vital signs
  static async getAlerts(hours: number = 24): Promise<any> {
    const result = await this.request(`/vital-signs/analytics/alerts?hours=${hours}`);
    return result.data;
  }

  // Helper methods for formatting and status
  static getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'normal': return '#10b981';
      case 'attention needed': return '#f59e0b';
      case 'abnormal': return '#ef4444';
      case 'critical': return '#dc2626';
      default: return '#6b7280';
    }
  }

  static getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'normal': return '✅';
      case 'attention needed': return '⚠️';
      case 'abnormal': return '🔴';
      case 'critical': return '🚨';
      default: return '📊';
    }
  }

  static getPriorityColor(severity: string): string {
    switch (severity.toLowerCase()) {
      case 'normal': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'critical': return '#ef4444';
      default: return '#6b7280';
    }
  }

  static formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}

export default VitalSignsService;